import { GuideMenu } from '@/components/GuideMenu';
import { createFileRoute } from '@tanstack/react-router';

const OverviewSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Nobility & Honor: From Peasant to Prince
    </h3>
    <h4 className="text-lg font-semibold text-[#f0d060]">An Overview</h4>
    <p className="text-[#b8b8b8] mb-2">
      Honor is the foundation of nobility in Mythea. While you may choose the
      path of an outlaw, those who walk the righteous road will find unique
      rewards. Over time, your deeds—both virtuous and infamous—will shape your
      reputation across the land.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      As your honor grows, so too will your influence. You will earn Titles of
      Nobility, each bestowing greater benefits upon your province and its
      people. Rise through the ranks, and you may find yourself among the most
      revered rulers in the realm.
    </p>
  </div>
);

const ObtainingHonorSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Earning Honor</h3>
    <p className="text-[#b8b8b8] mb-2">
      Gaining honor may seem simple, but true recognition comes from challenging
      worthy opponents:
    </p>
    <ul className="text-[#b8b8b8] list-disc list-inside mb-2">
      <li>Successfully attacking provinces of similar size grants honor.</li>
      <li>Many thievery and magic operations also contribute to your honor.</li>
      <li>Attacking weaker provinces yields no rewards.</li>
      <li>Harassing much larger provinces will not earn you honor, either.</li>
    </ul>
    <p className="text-[#b8b8b8] mb-2">
      Those who achieve the greatest honor are those who fight not just for
      themselves, but for the strength and unity of their Kingdom.
    </p>
  </div>
);

const TitlesSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Titles of Nobility
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      As you achieve victories in battle, magic, and espionage, your honor will
      grow, earning you prestigious Titles of Nobility.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      However, honor is not permanent—it can be lost just as easily as it is
      gained. If your honor falls below the required threshold, you may be
      stripped of your noble status.
    </p>
    <div className="overflow-x-auto mt-6">
      <table className="w-full">
        <thead>
          <tr className="text-left border-b border-white/10">
            <th className="p-2 text-[#f0d060]">Honor Level</th>
            <th className="p-2 text-[#f0d060]">Title</th>
          </tr>
        </thead>
        <tbody>
          {[
            { honor: '0 - 800', title: 'Peasant' },
            { honor: '801 - 1500', title: 'Knight / Lady' },
            { honor: '1501 - 2250', title: 'Lord / Noble Lady' },
            { honor: '2251 - 3000', title: 'Baron / Baroness' },
            { honor: '3001 - 3750', title: 'Viscount / Viscountess' },
            { honor: '3751 - 4500', title: 'Count / Countess' },
            { honor: '4501 - 5500', title: 'Marquis / Marchioness' },
            { honor: '5501 - 7000', title: 'Duke / Duchess' },
            { honor: '7000 - 10,000', title: 'Prince / Princess' },
            { honor: '10,000+', title: 'Crown Prince / Crown Princess' },
          ].map((row, index) => (
            <tr
              key={row.title}
              className={`border-b border-[rgba(212,175,55,0.12)] transition-colors duration-150 hover:bg-gray-700/30 ${
                index % 2 === 0 ? 'bg-white/[0.03]' : 'bg-white/[0.03]'
              }`}
            >
              <td className="p-2 text-[#b8b8b8]">{row.honor}</td>
              <td className="p-2 text-yellow-300">{row.title}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const BenefitsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      The Benefits of Honor
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      Honor is more than just prestige—it grants real advantages to your
      province. As you ascend the ranks of nobility, your people will rally
      behind your leadership, enhancing your province&apos;s efficiency and
      strength.
    </p>
    <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10 mt-4">
      <h4 className="text-lg font-semibold text-[#f0d060] mb-2">
        Honor Bonuses by Title
      </h4>
      <p className="text-[#b8b8b8] mb-4">
        Each title above Knight or Lady adds one step: population, income and
        offensive military efficiency rise by 2%, magic and thievery
        effectiveness by 3%, and voting weight by one.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-white/10">
              <th className="p-2 text-[#f0d060]">Title</th>
              <th className="p-2 text-[#f0d060]">Population / Income / Offense</th>
              <th className="p-2 text-[#f0d060]">Magic / Thievery</th>
              <th className="p-2 text-[#f0d060]">Votes</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Lord / Lady', '+2%', '+3%', '2'],
              ['Baron / Baroness', '+4%', '+6%', '3'],
              ['Viscount / Viscountess', '+6%', '+9%', '4'],
              ['Count / Countess', '+8%', '+12%', '5'],
              ['Marquis / Marchioness', '+10%', '+15%', '6'],
              ['Duke / Duchess', '+12%', '+18%', '7'],
              ['Prince / Princess', '+14%', '+21%', '8'],
              ['Crown Prince / Crown Princess', '+16%', '+24%', '9'],
            ].map(([title, core, magic, votes]) => (
              <tr key={title} className="border-b border-[rgba(212,175,55,0.12)]">
                <td className="p-2 text-[#b8b8b8]">{title}</td>
                <td className="p-2 text-[#b8b8b8]">{core}</td>
                <td className="p-2 text-[#b8b8b8]">{magic}</td>
                <td className="p-2 text-[#b8b8b8]">{votes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

export const Route = createFileRoute('/nobility/')({
  component: NobilityPage,
});

function NobilityPage() {
  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        <GuideMenu currentPath="/nobility" />

        {/* Main content section */}
        <section className="glass rounded-2xl p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <img
              src="/images/nav/honor@2x.png"
              alt="Nobility"
              width={80}
              height={80}
              className="shrink-0"
            />
            <h2 className="text-2xl font-bold">Nobility</h2>
          </div>

          {/* Content sections */}
          <div className="space-y-12 text-[#b8b8b8] leading-relaxed">
            <OverviewSection />
            <ObtainingHonorSection />
            <TitlesSection />
            <BenefitsSection />
          </div>
        </section>
      </div>
    </div>
  );
}
